import React from 'react'
import { I18n } from '@front/volcanion/'

import { IconButton, Typography, Grid, Box, StepLabel, StepContent, Icon } from '@mui/material'
import {
  DateField,
  TextField,
  TextComponent,
  MenuSelectorField,
  SingleOptionSelectorField,
  ModelMenuSelectorField,
  ModelAutocompleteField,
  AddressAutocompleteField,
  NumberField
} from '@front/squirtle'

import CPUtils from '@front/squirtle/utils/CPUtils'

const Title = ({ titleFormatted }) =>
  <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 'h6.fontSize', color: 'text.title' }}>
    <Typography variant='h5'> {I18n.t('contract.label', { count: 1 })}</Typography>
    <Typography sx={{ color: 'primary.main', fontWeight: 630, ml: 1 }}>
      {titleFormatted}
    </Typography>
  </Box>

const Row1 = ({
  payment_types,
  isReadOnly,
  handleClickEnableCustomPayment,
  enableCustomPayment,
  verifyClientId,
}) =>
  <Grid container spacing={1} size={12}>
    <Grid size={2}>
      <TextField name={'client_id'} label={I18n.t('contract.companycontract')} required debounceTime={250} afterUserChange={verifyClientId} />
    </Grid>
    <Grid size={2}>
      <TextField name={'name'} label={I18n.t('name.label', { count: 1 })} required />
    </Grid>
    <Grid size={3}>
      <TextField name={'contact.email'} label={I18n.t('email.label', { count: 1 })} />
    </Grid>
    <Grid size="grow">
      <ModelMenuSelectorField
        name={'formula'}
        label={I18n.t('commercial_formula.label', { count: 1 })}
        config={{ sort: ['name ASC'] }}
        model_name={'commercialformula'}
        labelKeys={['name']}
        loadOnMount
        searchMode={'search'}
        required
      />
    </Grid>
    <Grid hidden={_.isEmpty(payment_types) && !enableCustomPayment} size="grow">
      <ModelAutocompleteField
        name={'payment_types'}
        label={I18n.t('bank.payment_methods')}
        model_name={'paymenttype'}
        searchMode={'search'}
        config={{ sort: ['display_index ASC'], forced_filter: { disabled: false } }}
        labelKeys={['name_translated']}
        multiple
        loadOnMount
      />
    </Grid>
    <Grid hidden={!_.isEmpty(payment_types) || !!enableCustomPayment} size="grow">
      <TextComponent
        disabled
        label={I18n.t('bank.payment_methods')}
        value={'Selon formule commerciale'}
        InputProps={{
          endAdornment: <IconButton onClick={handleClickEnableCustomPayment} hidden={!!isReadOnly}>
            <Icon fontSize='small' color='primary'> {'edit'} </Icon>
          </IconButton>
        }}
      />
    </Grid>
  </Grid>

const Row2 = ({ isManagingRecord, handleDirectAccessChange, handleDisplayListChange, isReadOnly }) =>
  <Grid container spacing={1} size={12}>
    <Grid size="grow">
      <DateField
        name={'availableSince'}
        label={I18n.t('contract.available.since')}
        inputFormat={'DD/MM/YYYY'}
        disableOpenPicker={!!isReadOnly}
        required
      />
    </Grid>
    <Grid size="grow">
      <DateField
        name={'availableUntil'}
        label={I18n.t('contract.available.until')}
        inputFormat={'DD/MM/YYYY'}
        disableOpenPicker={!!isReadOnly}
        disablePast
      />
    </Grid>
    <Grid size="grow">
      <MenuSelectorField
        label={I18n.t('status.label', { count: 1 })}
        name={'status'}
        options={_.compact([
          { label: <Box color='green' fontWeight={500}> {I18n.t('status.active')}  </Box>, value: 'active' },
          { label: <Box color='orange' fontWeight={500}> {I18n.t('status.inactive')}  </Box>, value: 'inactive' },
          !!isManagingRecord && { label: <Box color='red' fontWeight={500}> {I18n.t('status.archive')}  </Box>, value: 'archive' },
        ])}
        selectFirst
        allowNone={false}
      />
    </Grid>
    <Grid size="grow">
      <SingleOptionSelectorField
        name={'direct_access'}
        label={I18n.t('contract.access.label.direct')}
        afterChange={handleDirectAccessChange}
      />
    </Grid>
    <Grid size="grow">
      <SingleOptionSelectorField
        name={'display_list'}
        label={I18n.t('contract.display_service_list')}
        afterChange={handleDisplayListChange}
      />
    </Grid>
    <Grid size="grow">
      <SingleOptionSelectorField
        name={'auth_account'}
        label={I18n.t('bank.deferred_billing')}
        clearOnEmpty={false}
      />
    </Grid>
    <Grid size="grow">
      <SingleOptionSelectorField
        name={'allow_auto_subscription'}
        label={I18n.t('contract.allow_auto_subscription')}
        clearOnEmpty={false}
      />
    </Grid>
  </Grid>

const Row3 = () =>
  <Grid container justifyContent='space-between' size={8}>
    <Grid size={3}>
      <NumberField
        name='customer_discount'
        label={I18n.t('companycontract.customer_discount')}
        type='float'
        minValue={0}
        maxValue={100}
        InputProps={{
          inputProps: { style: { textAlign: 'right' } },
          endAdornment: <Typography sx={{ fontSize: 'subtitle.fontSize', color: 'text.label' }}> {I18n.t('prefix.percent')} </Typography>
        }}
      />
    </Grid>
    <Grid size={3}>
      <NumberField
        name='driver_discount'
        label={I18n.t('companycontract.driver_discount')}
        type='float'
        minValue={0}
        maxValue={100}
        InputProps={{
          inputProps: { style: { textAlign: 'right' } },
          endAdornment: <Typography sx={{ fontSize: 'subtitle.fontSize', color: 'text.label' }}> {I18n.t('prefix.percent')} </Typography>
        }}
      />
    </Grid>
  </Grid>

const Row4 = ({ delayAtSourceType, delayAtSourceOptions, onDelayAtSourceTypeChange }) => {
  return (
    <Grid container spacing={1} alignItems='flex-end' size={12}>
      <Grid size={6}>
        <MenuSelectorField
          name={'ivr_access'}
          label={I18n.t('ivr_access.label', { count: 1 })}
          options={[
            { label: I18n.t('ivr_access.never'), value: '0' },
            { label: I18n.t('ivr_access.yes_if_order'), value: '1' },
            { label: I18n.t('ivr_access.yes_without_limit'), value: '2' },
            { label: I18n.t('commercial_formula.label.short'), value: null }
          ]}
          allowNone={false}
        />
      </Grid>
      <Grid size={3}>
        <MenuSelectorField
          name={'delayAtSourceType'}
          label={I18n.t('driver.waiting')}
          options={delayAtSourceOptions}
          afterChange={onDelayAtSourceTypeChange}
          alwaysInclude
          required
        />
      </Grid>
      <Grid hidden={delayAtSourceType === 'default'} size={3}>
        <NumberField
          label={I18n.t('driver.waiting_delay')}
          name={'delay_at_source'}
          type='number'
          minValue={0}
          required={delayAtSourceType === 'custom'}
          InputProps={{ endAdornment: 'min' }}
        />
      </Grid>
    </Grid >
  );
}

const Row5 = ({ onAddressResolve }) =>
  <Grid container spacing={1} size={12}>
    <Grid hidden={true} size={4}>
      <MenuSelectorField
        name={'invoice_type'}
        label={I18n.t('email.label.billing')}
        options={[
          { label: I18n.t('company.mode.company'), value: 'company' },
          { label: I18n.t('company.mode.contract'), value: 'contract' },
          { label: I18n.t('company.mode.do'), value: 'do' }]}
        required
      />
    </Grid>
    <Grid size="grow">
      <TextField name='contact.address' label={I18n.t('address.billing')} />
    </Grid>
    <Grid size="grow">
      <AddressAutocompleteField
        name={'address'}
        label={I18n.t('address.address_home')}
        noOptionsText={I18n.t('no_result')}
        resolveAddress
        onAddressResolve={onAddressResolve}
      />
    </Grid>
  </Grid>

const Row6 = () =>
  <Grid container size={12}>
    <Grid size="grow">
      <TextField name='comment.text' label={I18n.t('comment.admin')} />
    </Grid>
    <Grid size="grow">
      <SingleOptionSelectorField
        name={'exclude_heatmap'}
        label={I18n.t('heatmap.exclude_heatmap')}
        clearOnEmpty={false}
      />
    </Grid>
    <Grid size="grow">
      <SingleOptionSelectorField
        name={'bypass_heatmap_cascade'}
        label={I18n.t('heatmap.bypass_heatmap_cascade')}
        clearOnEmpty={false}
      />
    </Grid>
    <Grid size="grow">
      <SingleOptionSelectorField
        name='priority'
        label={I18n.t('priority.label.order')}
        clearOnEmpty={false}
      />
    </Grid>
  </Grid>

const Row7 = () =>
  <Grid container size={12} spacing={1}>
    <Grid size="grow">
      <ModelMenuSelectorField
        name='excluded_commercial_packages'
        model_name='commercialpackage'
        label={I18n.t('commercialpackage.label.exclude')}
        searchMode='search'
        multiple
        config={{
          initial_filter: {},
          mutator: cp => ({ ...CPUtils.getTranslatedLabel(cp), ...cp })
        }}
      />
    </Grid>
  </Grid>

const PrimarySection = props => <>
  <StepLabel>
    <Title {...props} />
  </StepLabel>
  <StepContent >
    <Box sx={{ p: 2, boxShadow: 6, borderRadius: 1 }}>
      <Grid container rowSpacing={2} >
        <Row1 {...props} />
        <Row2 {...props} />
        <Row3 {...props} />
        <Row4 {...props} />
        <Row5 {...props} />
        <Row6 {...props} />
        <Row7 />
      </Grid>
    </Box>
  </StepContent>
</>

export default React.memo(PrimarySection)
